import { AxiosError } from 'axios';

/**
 * Centralized error handling utilities for consistent error management across services
 * Follows Microsoft best practices for error handling and security
 */

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

export interface ErrorContext {
  operation: string;
  service: string;
  url?: string;
  method?: string;
  attempt?: number;
}

/**
 * Default retry configuration optimized for Microsoft Graph API
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2
};

/**
 * Enhanced error class for API operations with additional context
 */
export class ApiError extends Error {
  public readonly originalError?: Error;
  public readonly status?: number;
  public readonly url?: string;
  public readonly method?: string;
  public readonly service: string;
  public readonly isRetryable: boolean;

  constructor(
    message: string,
    service: string,
    options: {
      originalError?: Error;
      status?: number;
      url?: string;
      method?: string;
      isRetryable?: boolean;
    } = {}
  ) {
    super(message);
    this.name = 'ApiError';
    this.service = service;
    this.originalError = options.originalError;
    this.status = options.status;
    this.url = options.url;
    this.method = options.method;
    this.isRetryable = options.isRetryable ?? this.determineRetryability();
  }

  private determineRetryability(): boolean {
    if (!this.status) return false;

    // Microsoft Graph retryable status codes
    const retryableStatuses = [429, 500, 502, 503, 504];
    return retryableStatuses.includes(this.status);
  }
}

/**
 * Determines if an error should trigger a retry attempt
 */
export const isRetryableError = (error: any): boolean => {
  if (error instanceof ApiError) {
    return error.isRetryable;
  }

  // Handle Axios errors
  if (error?.response?.status) {
    const status = error.response.status;
    const retryableStatuses = [429, 500, 502, 503, 504];
    return retryableStatuses.includes(status);
  }

  // Handle network errors
  if (error?.code === 'NETWORK_ERROR' || error?.code === 'ECONNABORTED') {
    return true;
  }

  // Handle timeout errors
  if (error?.message?.toLowerCase().includes('timeout')) {
    return true;
  }

  return false;
};

/**
 * Determines if an error should NOT trigger a retry (non-retryable errors)
 */
export const isNonRetryableError = (error: any): boolean => {
  if (error instanceof ApiError && !error.isRetryable) {
    return true;
  }

  if (error?.response?.status) {
    const status = error.response.status;
    // Don't retry on auth, client, or not found errors
    const nonRetryableStatuses = [400, 401, 403, 404, 422];
    return nonRetryableStatuses.includes(status);
  }

  if (error?.message) {
    const message = error.message.toLowerCase();
    // Don't retry on permission or validation errors
    return message.includes('unauthorized') ||
           message.includes('forbidden') ||
           message.includes('bad request') ||
           message.includes('not found') ||
           message.includes('permission');
  }

  return false;
};

/**
 * Sanitizes error messages for safe logging/display
 * Prevents sensitive information exposure
 */
export const sanitizeError = (error: any): string => {
  if (!error) return 'Unknown error occurred';

  // For ApiError instances, use the sanitized message
  if (error instanceof ApiError) {
    return error.message;
  }

  // Handle Axios errors
  if (error?.response?.status) {
    const status = error.response.status;
    switch (status) {
      case 401:
        return 'Authentication required - please sign in again';
      case 403:
        return 'Access denied - insufficient permissions';
      case 404:
        return 'Resource not found';
      case 429:
        return 'Too many requests - please wait and try again';
      case 500:
        return 'Server error - please try again later';
      default:
        return `Request failed with status ${status}`;
    }
  }

  // Handle network errors
  if (error?.code === 'NETWORK_ERROR') {
    return 'Network connection error - please check your connection';
  }

  // Handle timeout errors
  if (error?.message?.toLowerCase().includes('timeout')) {
    return 'Request timed out - please try again';
  }

  // Generic fallback for unknown errors
  return 'An unexpected error occurred';
};

/**
 * Logs errors with appropriate context and sanitization
 */
export const logError = (error: any, context: ErrorContext): void => {
  const sanitizedMessage = sanitizeError(error);
  const logMessage = `[${context.service}] ${context.operation} failed`;

  if (context.url) {
    console.error(`${logMessage}: ${context.url}`, sanitizedMessage);
  } else {
    console.error(logMessage, sanitizedMessage);
  }

  // Log additional context for debugging (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.debug('Error context:', {
      service: context.service,
      operation: context.operation,
      url: context.url,
      method: context.method,
      attempt: context.attempt,
      originalError: error
    });
  }
};

/**
 * Calculates retry delay with exponential backoff and jitter
 */
export const calculateRetryDelay = (
  config: RetryConfig,
  attempt: number,
  error?: any
): number => {
  // Check for Retry-After header (Microsoft Graph throttling)
  if (error?.response?.headers?.['retry-after']) {
    const retryAfter = parseInt(error.response.headers['retry-after']);
    if (!isNaN(retryAfter)) {
      const retryAfterMs = retryAfter * 1000; // Convert seconds to milliseconds
      return Math.min(retryAfterMs, config.maxDelay);
    }
  }

  // Exponential backoff with jitter
  const exponentialDelay = config.baseDelay * Math.pow(config.backoffMultiplier, attempt - 1);
  const jitter = Math.random() * 0.1 * exponentialDelay; // Add 10% jitter
  const delay = exponentialDelay + jitter;

  return Math.min(delay, config.maxDelay);
};

/**
 * Creates a standardized error handler for service methods
 */
export const createErrorHandler = (service: string) => {
  return (error: any, operation: string, url?: string, method?: string): never => {
    const apiError = new ApiError(
      sanitizeError(error),
      service,
      {
        originalError: error,
        status: error?.response?.status,
        url,
        method
      }
    );

    logError(error, { operation, service, url, method });
    throw apiError;
  };
};

/**
 * Sleep utility for retry delays
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};
